import { useRef } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  packageBounceOf,
  useLiveMigrationRefresh,
  useRecentEvents,
  useSignoffWatch,
  type LiveEventsFeed,
} from "../api/hooks";
import type { Migration, MigrationState } from "../api/types";
import { Badge, Banner, Page, StageNav } from "../components";
import { t } from "../i18n/t";
import { LiveActivity } from "./LiveActivity";
import { MigrationStateBadge, pauseResumeText } from "./migrationState";
import { pathForStage, stageForState, stageLabel, stageSteps, type Stage } from "./stageRoute";

/**
 * The states in which a finished step hands the operator over: the sign-off gates and hypercare. An allowlist, not
 * "any waiting state": `failed` is waiting but is not a step that is ready, and terminal states offer nothing.
 */
const NEXT_STEP_STATES: ReadonlySet<MigrationState> = new Set<MigrationState>([
  "awaiting_scope_signoff",
  "awaiting_mapping_review",
  "awaiting_conversion_signoff",
  "hypercare",
]);

/**
 * The shared chrome of the four migration screens (Scope/ScopeFx, Review, DryRun, Status/StatusFailed —
 * guide §5 step 6): the breadcrumb ("Migrations / MIG-…" in mono, full id in the tooltip), the h1 with the
 * state pill beside it, a muted mono meta line or an action on the right, and the five-step stage rail
 * (connect → scope → review → dry run → status; a stopped migration marks the stage it stopped at).
 */
export function MigrationPage({
  id,
  migration,
  title,
  meta,
  actions,
  announce,
  stoppedStage,
  activity = true,
  eventsFeed,
  children,
}: {
  id: string;
  migration: Migration | undefined;
  /** The h1 — the client organisation on Scope/Status, the screen name on Review/DryRun. */
  title: string;
  /** The muted mono line on the right (route, conversion, attempt…). */
  meta?: string;
  /** Right-side controls instead of meta (DryRun's comparison-run select). */
  actions?: React.ReactNode;
  announce?: string;
  /** The stage a failed/cancelled migration stopped at — its rail step reads "stopped". */
  stoppedStage?: Stage | null;
  /** xmg-webux: the compact live activity strip; off where the screen shows the full timeline (Status). */
  activity?: boolean;
  /** xmg-lmux (3): the screen's own full events feed, so the live refresh does not poll a second one (Status). */
  eventsFeed?: LiveEventsFeed;
  children: React.ReactNode;
}): JSX.Element {
  // xmg-webux (defect 5): a change to the polled migration row refetches everything this screen shows.
  useLiveMigrationRefresh(id, migration, eventsFeed);
  const state = (migration?.state ?? "draft") as MigrationState;
  const pause = migration?.pause;
  // xmg-actions-first: a sign-off the server accepted is "Signing off" in the header and a status banner until the
  // state moves; a conversion sign-off the gate bounced is explained, not silent. Both read the shared stores
  // (the sign-off watch, the recent-events query LiveActivity already polls), so whichever screen is open shows them.
  const signoffWatch = useSignoffWatch(id, migration?.state);
  // Where the screen has its own full feed (Status) that one is read; no second events poll is started for it.
  const recent = useRecentEvents(
    id === "" || eventsFeed !== undefined ? null : id,
    migration?.state as MigrationState | undefined,
  );
  const bounce = packageBounceOf(
    eventsFeed?.events ?? recent.data ?? [],
    migration?.state as MigrationState | undefined,
  );
  // The screen the operator is on moved on without them (they signed off and the worker has now finished):
  // OFFER the next step -- a banner with a link, never a silent redirect away from what they are reading.
  const pageStage = useLocation().pathname.split("/").filter(Boolean).pop();
  const firstState = useRef<MigrationState | undefined>(undefined);
  if (firstState.current === undefined && migration !== undefined) {
    firstState.current = state;
  }
  const nextStage = stageForState(state);
  const nextStepReady =
    firstState.current !== undefined &&
    firstState.current !== state &&
    NEXT_STEP_STATES.has(state) &&
    nextStage !== "connect" &&
    nextStage !== pageStage;
  return (
    <Page
      breadcrumb={
        <nav className="breadcrumb" aria-label={t("a11y.breadcrumb")}>
          <Link to="/">{t("operator.title")}</Link> <span aria-hidden="true">/</span>{" "}
          <span className="mono" title={id}>
            {id.slice(0, 8)}
          </span>
          {migration?.source_org_name || migration?.destination_org_name ? (
            <span className="breadcrumb__orgs" data-testid="header-orgs">
              {" "}
              <span aria-hidden="true">·</span> <OrgName name={migration.source_org_name} />
              {" → "}
              <OrgName name={migration.destination_org_name} />
            </span>
          ) : null}
        </nav>
      }
      title={title}
      titleAccessory={
        <span data-testid="status-state">
          <MigrationStateBadge state={state} pause={pause} />
          {signoffWatch.waiting ? (
            <span data-testid="header-signing-off">
              <Badge tone="run" dot>
                {t("signoff.in_progress_header")}
              </Badge>
            </span>
          ) : null}
          {pause === null || pause === undefined ? null : (
            <span className="muted pausenote" data-testid="status-pause-note">
              {pauseResumeText(pause)}
            </span>
          )}
        </span>
      }
      actions={
        meta === undefined && actions === undefined ? undefined : (
          <>
            {meta === undefined ? null : (
              <span className="pagemeta" data-live>
                {meta}
              </span>
            )}
            {actions}
          </>
        )
      }
      headerBottom={
        <StageNav steps={stageSteps(id, state, stoppedStage, pause !== null && pause !== undefined)} />
      }
      announce={announce}
    >
      {nextStepReady ? (
        <div data-testid="next-step-ready">
          <Banner tone="info">
            {t("nextstep.ready", { step: stageLabel(nextStage) })}{" "}
            <Link to={pathForStage(id, nextStage)}>{t("nextstep.go", { step: stageLabel(nextStage) })}</Link>
          </Banner>
        </div>
      ) : null}
      {signoffWatch.waiting ? (
        <div role="status" data-testid="signing-off-banner">
          <Banner tone="info" title={t("signoff.in_progress_header")}>
            {t("signoff.in_progress_banner")}
          </Banner>
        </div>
      ) : null}
      {bounce === "rerunning" || bounce === "rerunning_after_signoff" ? (
        <div role="status" data-testid="package-changed-banner">
          <Banner tone="info">
            {bounce === "rerunning"
              ? t("signoff.package_changed_rerun")
              : t("signoff.package_changed_after_signoff")}
          </Banner>
        </div>
      ) : null}
      {(bounce === "returned" || bounce === "returned_after_signoff") && !signoffWatch.waiting ? (
        <div role="status" data-testid="package-rerun-done-banner">
          <Banner tone="info">
            {bounce === "returned"
              ? t("signoff.package_rerun_done")
              : t("signoff.package_rerun_done_after_signoff")}
          </Banner>
        </div>
      ) : null}
      {activity && migration !== undefined ? <LiveActivity id={id} migration={migration} /> : null}
      {children}
    </Page>
  );
}

/** An organisation name that may be long: ellipsised by CSS, the full text in `title`; absent -> an en dash. */
function OrgName({ name }: { name: string | null | undefined }): JSX.Element {
  if (name === null || name === undefined || name === "") return <span aria-hidden="true">–</span>;
  return (
    <span className="breadcrumb__org" title={name}>
      {name}
    </span>
  );
}
